import reactUpdate from 'immutability-helper';
import {useMemo, Dispatch, SetStateAction} from 'react';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {Endpoints} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {BulkAssemblyOrientation} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {FragmentModalFragment} from 'molbio/bulk-assembly/polymer-map-modals/FragmentCardContents';

export interface FragmentDataSetters {
  setPreferredPrimer5Id: (id: string | null) => void;
  setPreferredPrimer3Id: (id: string | null) => void;
  setRestrictionEnzymeId5: (id: number) => void;
  setRestrictionEnzymeId3: (id: number) => void;
  setEndpoints: (endpoints: Endpoints) => void;
  setOrientation: (orientation: BulkAssemblyOrientation) => void;
}

/**
 * Hook for mutating FragmentData state using stable callbacks.
 *
 * This also serves as a convenient place to fire analytics events.
 *
 * @param setFragmentsData Setter for controlling the list of fragments.
 * @param assemblyId Id of the current assembly. Used for analytics.
 */
export default function useFragmentDataSetters(
  setFragmentsData: Dispatch<SetStateAction<Array<FragmentModalFragment>>>,
  assemblyId: string,
  fragmentIndex: number
): FragmentDataSetters {
  return useMemo(
    () => ({
      setPreferredPrimer5Id: (preferredPrimer5Id: string | null) => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PREFERRED_PRIMER, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_FRAGMENT_MODAL,
          assemblyId,
          end: '5_prime',
        });
        setFragmentsData((prevFragmentsData: Array<FragmentModalFragment>) =>
          reactUpdate(prevFragmentsData, {[fragmentIndex]: {preferredPrimer5Id: {$set: preferredPrimer5Id}}})
        );
      },
      setPreferredPrimer3Id: (preferredPrimer3Id: string | null) => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_PREFERRED_PRIMER, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_FRAGMENT_MODAL,
          assemblyId,
          sequencePrime: '3_prime',
        });
        setFragmentsData((prevFragmentsData: Array<FragmentModalFragment>) =>
          reactUpdate(prevFragmentsData, {[fragmentIndex]: {preferredPrimer3Id: {$set: preferredPrimer3Id}}})
        );
      },
      setRestrictionEnzymeId5: (restrictionEnzymeId5: number) => {
        setFragmentsData((prevFragmentsData: Array<FragmentModalFragment>) =>
          reactUpdate(prevFragmentsData, {
            [fragmentIndex]: {restrictionEnzymeId5: {$set: restrictionEnzymeId5}},
          })
        );
      },
      setRestrictionEnzymeId3: (restrictionEnzymeId3: number) => {
        setFragmentsData((prevFragmentsData: Array<FragmentModalFragment>) =>
          reactUpdate(prevFragmentsData, {
            [fragmentIndex]: {restrictionEnzymeId3: {$set: restrictionEnzymeId3}},
          })
        );
      },
      setOrientation: (orientation: BulkAssemblyOrientation) => {
        setFragmentsData((prevFragmentsData: Array<FragmentModalFragment>) =>
          reactUpdate(prevFragmentsData, {[fragmentIndex]: {orientation: {$set: orientation}}})
        );
      },
      setEndpoints: (endpoints: Endpoints) => {
        setFragmentsData((prevFragmentsData: Array<FragmentModalFragment>) =>
          reactUpdate(prevFragmentsData, {[fragmentIndex]: {endpoints: {$set: endpoints}}})
        );
      },
    }),
    [setFragmentsData, assemblyId, fragmentIndex]
  );
}
